import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MessageSquare, Trophy, Users, Tv, Mail, Lock } from "lucide-react";

const DEMO_MAILTO =
  "mailto:operations@titanhavengroup.com?subject=Turf%20Talk%20Demo%20Request";

const FEATURES = [
  { icon: MessageSquare, title: "Game conversations", body: "Talk through every drive with your people in dedicated game rooms and private threads." },
  { icon: Trophy, title: "Make your picks", body: "Test your read on the game and see how you stack up against the room over the season." },
  { icon: Users, title: "Rivalry leagues", body: "Draft your crew into private leagues and settle who really knows football." },
  { icon: Tv, title: "The Character Show", body: "A branded studio cast breaks down the matchups with real personality and locked picks." },
];

export default function Landing() {
  const [params] = useSearchParams();
  const next = params.get("next");
  const signInHref = next ? `/login?next=${encodeURIComponent(next)}` : "/login";

  return (
    <div className="min-h-screen bg-[#0B1220] text-white flex flex-col" data-testid="public-landing">
      {/* ambient game-day cues */}
      <div aria-hidden className="fixed inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: "repeating-linear-gradient(90deg, #ffffff 0 2px, transparent 2px 120px)" }} />
      <div aria-hidden className="fixed -top-40 -right-40 w-[36rem] h-[36rem] rounded-full blur-3xl opacity-20 pointer-events-none" style={{ background: "radial-gradient(circle, #2F7D32 0%, transparent 70%)" }} />
      <div aria-hidden className="fixed -bottom-40 -left-40 w-[32rem] h-[32rem] rounded-full blur-3xl opacity-[0.12] pointer-events-none" style={{ background: "radial-gradient(circle, #14364e 0%, transparent 70%)" }} />

      {/* Top brand bar */}
      <header className="relative z-10 flex items-center justify-between px-6 sm:px-10 lg:px-16 py-6">
        <div className="flex items-center gap-4">
          <img src="/tt-mic-cropped.png" alt="Turf Talk" className="h-16 lg:h-[76px] w-auto object-contain" data-testid="landing-header-logo" />
          <div className="flex flex-col">
            <span className="font-display font-bold text-3xl lg:text-4xl leading-none tracking-wide">TURF TALK</span>
            <span className="font-semibold text-xs lg:text-sm tracking-[0.02em] text-white/85 mt-1">by SportChant</span>
          </div>
        </div>
        <a href={DEMO_MAILTO} data-testid="header-request-demo" className="hidden sm:inline-flex items-center gap-2 px-5 h-11 rounded-full bg-[#2F7D32] hover:bg-[#276b2a] text-white font-display tracking-wide text-sm transition-colors">
          <Mail size={16} /> REQUEST A DEMO
        </a>
      </header>

      {/* Hero */}
      <main className="relative z-10 flex-1 flex flex-col justify-center px-6 sm:px-10 lg:px-16 py-10 max-w-6xl">
        <div className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full border border-[#2F7D32]/50 bg-[#2F7D32]/10 mb-6" data-testid="beta-badge">
          <Lock size={13} className="text-[#4ea152]" />
          <span className="text-[11px] tracking-[0.22em] text-[#4ea152] font-display">CLOSED BETA · INVITE ONLY</span>
        </div>

        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.98] max-w-3xl">
          Every game has a conversation.<br />
          <span className="text-[#4ea152]">You're invited to join it.</span>
        </h1>

        <p className="text-white/65 text-base lg:text-lg mt-6 max-w-2xl leading-relaxed">
          Turf Talk is a closed-beta football conversation and prediction experience by SportChant —
          a place to follow the games you love, talk them through with your people, make your picks,
          run rivalry leagues, and watch our branded Character Show break down the matchups.
        </p>

        <p className="text-white/40 text-sm mt-3 max-w-2xl">
          Turf Talk is in private beta and access is granted by invitation. Inside the beta you'll
          follow the current NFL and college-football slate, with character picks and rooms built on
          our sports-data provider. This is an evolving prototype — some features are still being rolled out.
        </p>

        <div className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <a href={DEMO_MAILTO} data-testid="primary-request-demo" className="inline-flex items-center gap-2.5 px-7 h-14 rounded-full bg-[#2F7D32] hover:bg-[#276b2a] text-white font-display text-lg tracking-wide transition-colors shadow-lg shadow-[#2F7D32]/20">
            <Mail size={20} /> REQUEST A DEMO
          </a>
          <Link to={signInHref} data-testid="invited-signin" className="text-white/55 hover:text-white text-sm transition-colors underline underline-offset-4 decoration-white/20 hover:decoration-white/60">
            Already invited to the beta? Sign in
          </Link>
        </div>

        {/* Feature cards — concept descriptions only, no live/seeded data */}
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-sm" data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="w-10 h-10 rounded-xl bg-[#24362A] grid place-items-center mb-3">
                  <Icon size={18} className="text-[#4ea152]" />
                </div>
                <div className="font-display text-lg leading-tight mb-1.5">{f.title}</div>
                <p className="text-white/55 text-sm leading-snug">{f.body}</p>
              </div>
            );
          })}
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 px-6 sm:px-10 lg:px-16 py-8 border-t border-white/10 mt-10">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="text-white/40 text-xs" data-testid="landing-footer">A SportChant experience · Designed and built by Titan Haven Group</div>
          <a href={DEMO_MAILTO} className="text-[#4ea152] hover:underline text-xs" data-testid="footer-request-demo">Request a demo · operations@titanhavengroup.com</a>
        </div>
      </footer>
    </div>
  );
}
